import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateLoanActivity } from '@/hooks/lib/invalidate-loan-activity';
import { api } from '@/lib/api';
import { resolveActiveTenantId } from '@/lib/tenant-context';
import { useAuthStore } from '@/stores/auth-store';

export interface FundingLine {
  id: string;
  name: string;
  provider: string;
  facilityLimit: number;
  drawnAmount: number;
  availableAmount: number;
  costRate: number;
  rateType?: string;
  baseRateName?: string;
  margin?: number;
  startDate: string;
  maturityDate?: string;
  fundId?: string;
  notes?: string;
  status: 'active' | 'inactive' | 'matured';
  utilisationPercent: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFundingLineDto {
  name: string;
  provider: string;
  facilityLimit: number;
  costRate: number;
  rateType?: string;
  baseRateName?: string;
  margin?: number;
  startDate: string;
  maturityDate?: string;
  fundId?: string;
  notes?: string;
}

export interface UpdateFundingLineDto {
  name?: string;
  provider?: string;
  facilityLimit?: number;
  costRate?: number;
  rateType?: string;
  baseRateName?: string;
  margin?: number;
  startDate?: string;
  maturityDate?: string;
  fundId?: string;
  notes?: string;
}

export interface LoanFunding {
  id: string;
  loanId: string;
  fundingLineId: string;
  fundingLineName: string;
  allocatedAmount: number;
  costRate: number;
  startDate: string;
  endDate?: string;
}

export interface LoanFundingSummary {
  totalAllocated: number;
  loanPrincipal: number;
  fundedPercentage: number;
  weightedCostRate: number;
  loanRate: number;
  nim: number;
  nimBps: number;
  annualNimDollars: number;
}

export interface LoanFundingResponse {
  allocations: LoanFunding[];
  summary: LoanFundingSummary;
}

type RawFundingLine = Omit<
  FundingLine,
  | 'facilityLimit'
  | 'drawnAmount'
  | 'availableAmount'
  | 'costRate'
  | 'margin'
  | 'status'
  | 'utilisationPercent'
> & {
  facilityLimit: number | string;
  drawnAmount: number | string;
  availableAmount?: number | string | null;
  costRate: number | string;
  margin?: number | string | null;
  status: string;
  utilisationPercent?: number | string | null;
};

type RawLoanFunding = {
  id: string;
  loanId: string;
  fundingLineId: string;
  allocatedAmount: number | string;
  effectiveDate?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  fundingLine?: {
    id?: string;
    name?: string | null;
    costRate?: number | string | null;
  } | null;
  fundingLineName?: string | null;
  costRate?: number | string | null;
};

type RawLoanFundingResponse =
  | LoanFunding[]
  | {
      allocations?: RawLoanFunding[] | null;
      summary?: Partial<LoanFundingSummary> | null;
    }
  | null
  | undefined;

function normalizeFundingLineStatus(status: string): FundingLine['status'] {
  const normalized = status.toLowerCase();

  if (normalized === 'active') {
    return 'active';
  }

  if (
    normalized === 'fully_drawn' ||
    normalized === 'expired' ||
    normalized === 'cancelled' ||
    normalized === 'matured'
  ) {
    return 'matured';
  }

  return 'inactive';
}

function toDateInputValue(value?: string | null): string {
  if (!value) return '';

  const isoDate = value.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  if (isoDate) return isoDate;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().split('T')[0];
}

export function normalizeFundingLine(line: RawFundingLine): FundingLine {
  const facilityLimit = Number(line.facilityLimit ?? 0);
  const drawnAmount = Number(line.drawnAmount ?? 0);
  const availableAmount =
    line.availableAmount == null
      ? Math.max(0, facilityLimit - drawnAmount)
      : Number(line.availableAmount);
  const utilisationPercent =
    line.utilisationPercent != null
      ? Number(line.utilisationPercent)
      : facilityLimit > 0
        ? (drawnAmount / facilityLimit) * 100
        : 0;

  return {
    ...line,
    facilityLimit,
    drawnAmount,
    availableAmount,
    costRate: Number(line.costRate ?? 0),
    margin: line.margin == null ? undefined : Number(line.margin),
    startDate: toDateInputValue(line.startDate),
    maturityDate: toDateInputValue(line.maturityDate) || undefined,
    status: normalizeFundingLineStatus(line.status),
    utilisationPercent,
  };
}

function normalizeLoanFunding(allocation: RawLoanFunding): LoanFunding {
  return {
    id: allocation.id,
    loanId: allocation.loanId,
    fundingLineId: allocation.fundingLineId,
    fundingLineName:
      allocation.fundingLineName ?? allocation.fundingLine?.name ?? 'Unknown funding line',
    allocatedAmount: Number(allocation.allocatedAmount ?? 0),
    costRate: Number(allocation.costRate ?? allocation.fundingLine?.costRate ?? 0),
    startDate: allocation.startDate ?? allocation.effectiveDate ?? new Date(0).toISOString(),
    endDate: allocation.endDate ?? undefined,
  };
}

function normalizeLoanFundingResponse(payload: RawLoanFundingResponse): LoanFundingResponse {
  if (Array.isArray(payload)) {
    return {
      allocations: payload as LoanFunding[],
      summary: {
        totalAllocated: 0,
        loanPrincipal: 0,
        fundedPercentage: 0,
        weightedCostRate: 0,
        loanRate: 0,
        nim: 0,
        nimBps: 0,
        annualNimDollars: 0,
      },
    };
  }

  const summary = payload?.summary ?? {};

  return {
    allocations: Array.isArray(payload?.allocations)
      ? payload.allocations.map(normalizeLoanFunding)
      : [],
    summary: {
      totalAllocated: Number(summary.totalAllocated ?? 0),
      loanPrincipal: Number(summary.loanPrincipal ?? 0),
      fundedPercentage: Number(summary.fundedPercentage ?? 0),
      weightedCostRate: Number(summary.weightedCostRate ?? 0),
      loanRate: Number(summary.loanRate ?? 0),
      nim: Number(summary.nim ?? 0),
      nimBps: Number(summary.nimBps ?? 0),
      annualNimDollars: Number(summary.annualNimDollars ?? 0),
    },
  };
}

function unwrapListResponse<T>(
  payload: T[] | { data?: T[] | null; items?: T[] | null; results?: T[] | null } | null | undefined,
): T[] {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    return [];
  }

  if (Array.isArray(payload.data)) {
    return payload.data;
  }

  if (Array.isArray(payload.items)) {
    return payload.items;
  }

  if (Array.isArray(payload.results)) {
    return payload.results;
  }

  return [];
}

function invalidateLoanFundingAnalyticsQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  tenantId: string | null,
  loanId: string,
) {
  queryClient.invalidateQueries({ queryKey: ['loans', tenantId, loanId, 'funding'] });
  queryClient.invalidateQueries({ queryKey: ['loans', tenantId, loanId, 'cashflows'] });
  queryClient.invalidateQueries({ queryKey: ['loans', tenantId, loanId, 'npv'] });
  queryClient.invalidateQueries({ queryKey: ['loans', tenantId, loanId, 'yield'] });
  queryClient.invalidateQueries({ queryKey: ['funding-lines'] });
  queryClient.invalidateQueries({ queryKey: ['treasury'] });
}

function useActiveTenantId() {
  return useAuthStore(state => resolveActiveTenantId(state.user));
}

export function useFundingLines() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['funding-lines', tenantId],
    queryFn: async () => {
      const { data } = await api.get<RawFundingLine[]>('/treasury/funding-lines');
      return unwrapListResponse<RawFundingLine>(data).map(normalizeFundingLine);
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!tenantId,
  });
}

export function useFundingLine(id: string) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['funding-lines', tenantId, id],
    queryFn: async () => {
      const { data } = await api.get<RawFundingLine>(`/treasury/funding-lines/${id}`);
      return normalizeFundingLine(data);
    },
    enabled: !!tenantId && !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateFundingLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateFundingLineDto) => {
      const { data } = await api.post<RawFundingLine>('/treasury/funding-lines', input);
      return normalizeFundingLine(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['funding-lines'] });
      qc.invalidateQueries({ queryKey: ['treasury'] });
    },
  });
}

export function useUpdateFundingLine() {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: { id: string } & UpdateFundingLineDto) => {
      const { data } = await api.patch<RawFundingLine>(`/treasury/funding-lines/${id}`, input);
      return normalizeFundingLine(data);
    },
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['funding-lines'] });
      qc.invalidateQueries({ queryKey: ['funding-lines', tenantId, id] });
      qc.invalidateQueries({ queryKey: ['treasury'] });
    },
  });
}

export function useDeleteFundingLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/treasury/funding-lines/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['funding-lines'] });
      qc.invalidateQueries({ queryKey: ['treasury'] });
    },
  });
}

export function useLoanFunding(loanId: string) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['loans', tenantId, loanId, 'funding'],
    queryFn: async () => {
      const { data } = await api.get<RawLoanFundingResponse>(`/loans/${loanId}/funding`);
      return normalizeLoanFundingResponse(data);
    },
    enabled: !!tenantId && !!loanId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAllocateFunding(loanId: string) {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { fundingLineId: string; allocatedAmount: number }) => {
      const { data } = await api.post<LoanFunding>(`/loans/${loanId}/funding`, input);
      return data;
    },
    onSuccess: () => {
      invalidateLoanFundingAnalyticsQueries(qc, tenantId, loanId);
      invalidateLoanActivity(qc, loanId);
    },
  });
}

export function useDeallocateFunding(loanId: string) {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (allocationId: string) => {
      const { data } = await api.delete(`/loans/${loanId}/funding/${allocationId}`);
      return data;
    },
    onSuccess: () => {
      invalidateLoanFundingAnalyticsQueries(qc, tenantId, loanId);
      invalidateLoanActivity(qc, loanId);
    },
  });
}
